
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const WarrantyRegistrationPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Warranty Registration Process | Carolina Trophy Roofs</title>
        <meta name="description" content="Learn how to register your roofing warranty with manufacturers like GAF, Owens Corning, and IKO. Step-by-step guide to ensure your warranty protection is activated and maintained." />
        <meta name="keywords" content="warranty registration, roof warranty registration, GAF registration, Owens Corning registration, warranty activation" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/resources/warranty-registration" />
      </Helmet>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link to="/resources" className="text-primary hover:underline">Resources</Link>
            <span className="mx-2">→</span>
            <span className="text-muted-foreground">Warranty Registration Process</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <FileText className="mr-2 h-4 w-4" />
              Warranty Information
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Warranty Registration Process
            </h1>
            <p className="text-xl text-gray-600">
              Ensuring your warranty protection is properly activated and maintained
            </p>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3 mt-1" />
                <div>
                  <h2 className="text-xl font-semibold text-red-800 mb-2">Critical: Registration Deadlines</h2>
                  <p className="text-red-700">
                    Most manufacturer warranties <strong>must be registered within 60-90 days</strong> of installation 
                    or coverage may be voided. We handle this process for all our customers to ensure protection.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-6">Why Registration Matters</h2>
            <p className="mb-6">
              According to the <a href="https://www.nrca.net" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">National Roofing Contractors Association</a>, 
              warranty registration serves as legal proof of coverage and helps manufacturers track product performance. 
              The <a href="https://www.fema.gov" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Federal Emergency Management Agency (FEMA)</a> 
              also recommends maintaining warranty documentation for disaster recovery and insurance purposes.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-green-50 p-6 rounded-lg text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Legal Protection</h3>
                <p className="text-sm text-muted-foreground">
                  Establishes your rights under manufacturer warranty terms
                </p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg text-center">
                <Clock className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Claims Processing</h3>
                <p className="text-sm text-muted-foreground">
                  Faster warranty claim resolution and approval
                </p>
              </div>
              <div className="bg-purple-50 p-6 rounded-lg text-center">
                <FileText className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Product Tracking</h3>
                <p className="text-sm text-muted-foreground">
                  Manufacturers can notify you of recalls or updates
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Registration Timeline by Brand</h2>
            
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 p-3 text-left">Manufacturer</th>
                    <th className="border border-gray-300 p-3 text-left">Registration Deadline</th>
                    <th className="border border-gray-300 p-3 text-left">Registration Method</th>
                    <th className="border border-gray-300 p-3 text-left">Required Documents</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3 font-medium">GAF</td>
                    <td className="border border-gray-300 p-3">60 days</td>
                    <td className="border border-gray-300 p-3">Online/Mail</td>
                    <td className="border border-gray-300 p-3">Receipt, Installation certificate</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3 font-medium">Owens Corning</td>
                    <td className="border border-gray-300 p-3">90 days</td>
                    <td className="border border-gray-300 p-3">Online portal</td>
                    <td className="border border-gray-300 p-3">Purchase receipt, Product labels</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3 font-medium">IKO</td>
                    <td className="border border-gray-300 p-3">60 days</td>
                    <td className="border border-gray-300 p-3">Online/Phone</td>
                    <td className="border border-gray-300 p-3">Receipt, Contractor certification</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3 font-medium">Atlas</td>
                    <td className="border border-gray-300 p-3">60 days</td>
                    <td className="border border-gray-300 p-3">Online form</td>
                    <td className="border border-gray-300 p-3">Installation date, Product info</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3 font-medium">CertainTeed</td>
                    <td className="border border-gray-300 p-3">60 days</td>
                    <td className="border border-gray-300 p-3">Online/Mail</td>
                    <td className="border border-gray-300 p-3">SureStart certificate, Receipt</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold mb-4">Our Registration Service</h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
              <h3 className="text-xl font-semibold text-blue-800 mb-3">Complete Registration Management</h3>
              <p className="text-blue-700 mb-4">
                Carolina Trophy Roofs handles all warranty registrations for our customers at no additional cost. 
                This service ensures your warranties are properly activated and maintained.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2 text-blue-800">What We Handle</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Complete manufacturer registration forms</li>
                    <li>• Submit required documentation</li>
                    <li>• Verify registration confirmation</li>
                    <li>• Provide you with warranty certificates</li>
                    <li>• Maintain digital records for future reference</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2 text-blue-800">Your Responsibilities</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Provide accurate property information</li>
                    <li>• Keep warranty certificates safe</li>
                    <li>• Notify us of any address changes</li>
                    <li>• Report issues promptly</li>
                    <li>• Maintain proper roof care</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Step-by-Step Registration Process</h2>
            
            <div className="space-y-6 mb-8">
              <div className="flex items-start">
                <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1">
                  <span className="text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Project Completion & Documentation</h3>
                  <p className="text-muted-foreground">
                    Upon project completion, we compile all necessary documentation including installation 
                    certificates, material specifications, and purchase receipts.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1">
                  <span className="text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Registration Submission</h3>
                  <p className="text-muted-foreground">
                    We submit warranty registrations to all applicable manufacturers within 7 days of 
                    project completion, well ahead of all deadline requirements.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1">
                  <span className="text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Confirmation & Verification</h3>
                  <p className="text-muted-foreground">
                    We track registration confirmations and verify all warranties are properly activated. 
                    Any issues are resolved immediately.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1">
                  <span className="text-sm font-bold">4</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Customer Documentation Package</h3>
                  <p className="text-muted-foreground">
                    You receive a complete warranty package including all certificates, registration 
                    confirmations, and maintenance guidelines.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Required Information for Registration</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-3">Property Information</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Complete property address</li>
                  <li>• Property owner name and contact info</li>
                  <li>• Installation date</li>
                  <li>• Square footage of roof</li>
                  <li>• Building type (residential/commercial)</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-3">Product Information</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Product names and model numbers</li>
                  <li>• Color and style specifications</li>
                  <li>• Quantity of materials used</li>
                  <li>• Purchase receipts and invoices</li>
                  <li>• Contractor licensing information</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Digital Warranty Management</h2>
            <p className="mb-4">
              Modern warranty management uses digital platforms for easier access and tracking. 
              The <a href="https://www.consumerreports.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Consumer Reports</a> 
              recommends maintaining digital copies of all warranty documents for quick access during claims.
            </p>

            <div className="bg-green-50 p-6 rounded-lg mb-8">
              <h3 className="text-lg font-semibold mb-3">Our Digital Warranty Portal</h3>
              <p className="text-sm text-muted-foreground mb-4">
                All Carolina Trophy Roofs customers receive access to our digital warranty portal where you can:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="text-sm space-y-1">
                  <li>• View all warranty certificates</li>
                  <li>• Access installation photos and documentation</li>
                  <li>• Submit warranty claims online</li>
                  <li>• Track claim status and history</li>
                </ul>
                <ul className="text-sm space-y-1">
                  <li>• Download warranty documents</li>
                  <li>• Update contact information</li>
                  <li>• Schedule maintenance reminders</li>
                  <li>• Access manufacturer resources</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Common Registration Issues</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">Avoiding Registration Problems</h3>
              <div className="grid md:grid-cols-2 gap-4 text-yellow-700">
                <div>
                  <h4 className="font-medium mb-2">Common Mistakes</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Missing registration deadline</li>
                    <li>• Incomplete documentation</li>
                    <li>• Incorrect product information</li>
                    <li>• Using non-certified contractors</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Our Prevention Methods</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Automatic registration tracking</li>
                    <li>• Complete documentation review</li>
                    <li>• Manufacturer verification</li>
                    <li>• Certified contractor credentials</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <h2 className="text-2xl font-bold mb-4">Ready to Protect Your Investment?</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Let us handle your warranty registration process to ensure complete protection.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to="/contact">Get Started Today</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/resources">
                    <FileText className="mr-2 h-5 w-5" />
                    More Resources
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WarrantyRegistrationPage;
